import React, { useState } from 'react';
import classnames from 'classnames';
import PopUpForm from 'js/components/elements/pop-up-form/PopUpForm';
import { filter, map } from 'lodash';
import { KeyboardArrowUp as ArrowUp, KeyboardArrowDown as ArrowDown } from '@mui/icons-material';
import styles from './FileFilters.module.scss';
import { ReactComponent as FilterListFilled } from 'new/components/icons/FilterListFilled.svg';
import { useFileFilters } from './useFileFilters';

const getFilterDisplayValue = (field, value) => {
  if (!value) return null;

  switch (field.type) {
    case 'boolean':
      return value ? 'Enabled' : 'Disabled';
    case 'select':
      const option = field.options?.find((opt) => opt.value === value);
      return option?.label || value;
    case 'datetime':
      return new Date(value).toLocaleString();
    case 'array':
      return Array.isArray(value) ? value.join(', ') : value;
    default:
      return value;
  }
};

const FileFilters = ({
  sourceConfig,
  onSourceConfigChanged,
  selectedNavScrollerSection,
  sourceConfigFields,
  selectedBucket,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useFileFilters({
    selectedBucket,
    sourceConfig,
    onSourceConfigChanged,
  });

  let processedFields = filter(
    sourceConfigFields,
    (field) => field.section === 'Apply File Filters',
  );

  const fields = processedFields;

  if (fields.length === 0) {
    return null;
  }

  const handleExpandButtonClicked = () => {
    setIsExpanded(!isExpanded);
  };

  const activeFilters = fields.filter((field) => {
    const value = sourceConfig[field.name];
    // Filter out fields without values and UI-only toggle fields
    return (
      value !== undefined && value !== null && value !== '' && field.name !== 'path_exclusions'
    );
  });

  const renderFilterSummary = () => {
    if (activeFilters.length === 0) {
      return <div className={styles.noFilters}>No filters applied</div>;
    }

    return (
      <div className={styles.filterSummary}>
        {map(activeFilters, (field) => {
          const value = getFilterDisplayValue(field, sourceConfig[field.name]);
          // Value is guaranteed to exist due to activeFilters filter, so we only need to check if getFilterDisplayValue returned null
          if (!value) return null;

          const displayName = field.display_name
            .split(':')[0]
            .replace(/\s*\([^)]*\)/, '')
            .replace(/\s*Eg\.?.*$/, '')
            .trim();

          return (
            <div key={field.name} className={styles.filterItem}>
              <span className={styles.filterName}>{displayName}:</span>
              <span className={styles.filterValue}>{value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      data-testid="folder-filters-container"
      className={`advanced-configuration ${!isExpanded ? 'is-collapsed' : ''} ${styles.container}`}
    >
      <div
        className={classnames(styles.filterHeader, {
          [styles.isExpanded]: isExpanded,
          [styles.hasFilters]: activeFilters.length > 0,
        })}
        onClick={handleExpandButtonClicked}
      >
        <div className={styles.headerContent}>
          <div className={styles.headerFilterButton}>
            <FilterListFilled />
            <span className={styles.headerFilterButtonText}>Folder Filters</span>
          </div>

          {!isExpanded && renderFilterSummary()}
        </div>

        <div className={styles.headerCollapseButton}>
          {isExpanded ? (
            <ArrowUp fontSize="large" className={styles.collapseButton} />
          ) : (
            <ArrowDown fontSize="large" className={styles.collapseButton} />
          )}
        </div>
      </div>
      {isExpanded && (
        <PopUpForm
          removeSectionHeading
          fields={fields}
          isModal={false}
          data={sourceConfig}
          showCloseButton={false}
          onFieldUpdated={(name, value, data) => onSourceConfigChanged(data, name, value)}
          onSourceConfigChanged={onSourceConfigChanged}
          isCredentialsForm={false}
          selectedNavScrollerSection={selectedNavScrollerSection}
        />
      )}
    </div>
  );
};

export default FileFilters;
